import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  Image as ImageIcon,
  Maximize2,
  Move,
  Paintbrush,
  Play,
  SlidersHorizontal,
  Sparkles,
  Square,
  Type,
} from 'lucide-react';

import { Button } from '@/components';

import { AnimationsSection } from './sections/AnimationsSection';
import { BorderSection } from './sections/BorderSection';
import { EffectsSection } from './sections/EffectsSection';
import { FillSection } from './sections/FillSection';
import { FiltersSection } from './sections/FiltersSection';
import { IconSection } from './sections/IconSection';
import { SizeSection } from './sections/SizeSection';
import { SpacingSection } from './sections/SpacingSection';
import { TypographySection } from './sections/TypographySection';
import { migrateOldStyle } from './styleToCSS';
import type { LayoutElementStyle } from './styleTypes';

interface StylePanelAdvancedProps {
  style: LayoutElementStyle | undefined;
  onChange: (style: LayoutElementStyle) => void;
  elementType?: string; // 'field' | 'section' | 'heading' | 'card' | etc.
}

const SECTIONS = [
  { id: 'typography', icon: Type },
  { id: 'fill', icon: Paintbrush },
  { id: 'border', icon: Square },
  { id: 'effects', icon: Sparkles },
  { id: 'spacing', icon: Move },
  { id: 'size', icon: Maximize2 },
  { id: 'animations', icon: Play },
  { id: 'filters', icon: SlidersHorizontal },
  { id: 'icon', icon: ImageIcon },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

export function StylePanelAdvanced({
  style: rawStyle,
  onChange,
  elementType,
}: StylePanelAdvancedProps) {
  const { t } = useTranslation();
  const style = migrateOldStyle(rawStyle || {});
  const [openSections, setOpenSections] = useState<Set<SectionId>>(new Set(['typography', 'fill']));

  const toggleSection = (id: SectionId) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleChange = (updates: Partial<LayoutElementStyle>) => {
    onChange({ ...style, ...updates });
  };

  const renderSection = (id: SectionId) => {
    switch (id) {
      case 'typography':
        return <TypographySection style={style} onChange={handleChange} />;
      case 'fill':
        return <FillSection style={style} onChange={handleChange} />;
      case 'border':
        return <BorderSection style={style} onChange={handleChange} />;
      case 'effects':
        return <EffectsSection style={style} onChange={handleChange} />;
      case 'spacing':
        return <SpacingSection style={style} onChange={handleChange} />;
      case 'size':
        return <SizeSection style={style} onChange={handleChange} />;
      case 'animations':
        return <AnimationsSection style={style} onChange={handleChange} />;
      case 'filters':
        return <FiltersSection style={style} onChange={handleChange} />;
      case 'icon':
        return <IconSection style={style} onChange={handleChange} elementType={elementType} />;
    }
  };

  // Check if section has any values set
  const sectionHasValues = (id: SectionId): boolean => {
    switch (id) {
      case 'typography':
        return !!(
          style.fontFamily ||
          style.fontSize ||
          style.fontWeight ||
          style.lineHeight ||
          style.letterSpacing ||
          style.textDecoration ||
          style.textTransform ||
          style.textColor
        );
      case 'fill':
        return !!(style.backgroundColor || style.gradient || style.backgroundImage);
      case 'border':
        return !!(
          style.borderWidth ||
          style.borderTopWidth ||
          style.borderColor ||
          (typeof style.borderRadius === 'number' && style.borderRadius > 0) ||
          style.borderTopLeftRadius
        );
      case 'effects':
        return !!(
          (style.boxShadows && style.boxShadows.length > 0) ||
          style.textShadow ||
          (style.opacity !== undefined && style.opacity < 100) ||
          style.backdropBlur
        );
      case 'spacing':
        return !!(
          style.paddingTop ||
          style.paddingRight ||
          style.paddingBottom ||
          style.paddingLeft ||
          typeof style.marginTop === 'number' ||
          style.marginRight ||
          typeof style.marginBottom === 'number' ||
          style.marginLeft ||
          style.gap
        );
      case 'size':
        return !!(
          style.width ||
          style.minHeight ||
          style.maxHeight ||
          (style.overflow && style.overflow !== 'visible')
        );
      case 'animations':
        return !!(style.entryAnimation && style.entryAnimation !== 'none') || !!style.hoverEffect;
      case 'filters':
        return !!(
          style.filterBlur ||
          style.filterBrightness ||
          style.filterContrast ||
          style.filterSaturate ||
          style.filterGrayscale ||
          style.filterHueRotate
        );
      case 'icon':
        return !!style.icon;
      default:
        return false;
    }
  };

  return (
    <div className="space-y-0.5">
      {SECTIONS.map(({ id, icon: Icon }) => {
        const isOpen = openSections.has(id);
        const hasValues = sectionHasValues(id);
        const label = t(`tenant.systemModuleBuilder.style.sections.${id}`);

        return (
          <div
            key={id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <Button
              type="button"
              variant="ghost"
              onClick={() => toggleSection(id)}
              className="w-full justify-start gap-2 rounded-none px-3 py-2 font-normal hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 flex-1 text-left">
                {label}
              </span>
              {hasValues && (
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0" />
              )}
              <ChevronDown
                className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
              />
            </Button>
            {isOpen && <div className="px-3 pb-3 pt-1">{renderSection(id)}</div>}
          </div>
        );
      })}
    </div>
  );
}
